import { CrmEntityType } from '@prisma/client';
import { uniqueBy, uniqueChunks } from './chunks';
import { CrmSyncRecordClient, DiscoveredRecord } from './types';

/**
 * Inserts owned by crm-discover. An invoice inserted terminal is permanently ineligible: its
 * lastSyncedAt remains null, so the invoice reopen query can never select it.
 */
export async function insertDiscoveredRecords({
  now,
  prisma,
  records,
}: {
  prisma: CrmSyncRecordClient;
  records: DiscoveredRecord[];
  now: Date;
}): Promise<number> {
  const uniqueRecords = uniqueBy(records, record => `${record.entityType}:${record.entityId}`);
  let count = 0;

  for (const chunk of uniqueChunks(uniqueRecords)) {
    const result = await prisma.crmSyncRecord.createMany({
      data: chunk.map(record => ({
        crmObjectId: null,
        dueAt: now,
        entityId: record.entityId,
        entityType: record.entityType,
        ...(record.entityType === CrmEntityType.INVOICE && record.isTerminal !== undefined
          ? { isTerminal: record.isTerminal }
          : {}),
      })),
      skipDuplicates: true,
    });
    count += result.count;
  }

  return count;
}

/** Pulls healthy future rows forward without undoing backoff or quarantine. */
export async function pullForwardDueAt({
  now,
  prisma,
  recordIds,
}: {
  prisma: CrmSyncRecordClient;
  recordIds: number[];
  now: Date;
}): Promise<number> {
  let count = 0;

  for (const ids of uniqueChunks(recordIds)) {
    const result = await prisma.crmSyncRecord.updateMany({
      data: { dueAt: now },
      where: {
        dueAt: { gt: now },
        failureCount: 0,
        id: { in: ids },
        isTerminal: false,
        quarantinedAt: null,
      },
    });
    count += result.count;
  }

  return count;
}

/** Reopens only rows selected by discovery as having changed after their last successful sync. */
export async function reopenTerminal({
  now,
  prisma,
  recordIds,
}: {
  prisma: CrmSyncRecordClient;
  recordIds: number[];
  now: Date;
}): Promise<number> {
  let count = 0;

  for (const ids of uniqueChunks(recordIds)) {
    const result = await prisma.crmSyncRecord.updateMany({
      data: { dueAt: now, isTerminal: false },
      where: { id: { in: ids }, isTerminal: true },
    });
    count += result.count;
  }

  return count;
}

/** Marks active platform rows deleted and schedules their final HubSpot flag write. */
export async function markPlatformDeleted({
  now,
  prisma,
  recordIds,
}: {
  prisma: CrmSyncRecordClient;
  recordIds: number[];
  now: Date;
}): Promise<number> {
  let count = 0;

  for (const ids of uniqueChunks(recordIds)) {
    const result = await prisma.crmSyncRecord.updateMany({
      data: { dueAt: now, isPlatformDeleted: true },
      where: { id: { in: ids }, isPlatformDeleted: false, isTerminal: false },
    });
    count += result.count;
  }

  return count;
}

/** Restore is the only path that clears both platform deletion and deletion terminality. */
export async function restorePlatformDeleted({
  now,
  prisma,
  recordIds,
}: {
  prisma: CrmSyncRecordClient;
  recordIds: number[];
  now: Date;
}): Promise<number> {
  let count = 0;

  for (const ids of uniqueChunks(recordIds)) {
    const result = await prisma.crmSyncRecord.updateMany({
      data: { dueAt: now, isPlatformDeleted: false, isTerminal: false },
      where: { id: { in: ids }, isPlatformDeleted: true },
    });
    count += result.count;
  }

  return count;
}
